// Band layout rules: how bands are split into pages, and how the content of a
// band moves when merged data makes elements taller, shorter or empty. See
// docs/bands-spec.md for the rules in prose; the editor preview and the PDF
// backend are meant to follow exactly these.
//
// Plain functions on numbers (px) only, no imports, no DOM: callers measure
// heights however they draw (browser, PDFKit) and pass them in.

const REPEAT = { EVERY_PAGE: "everyPage", FIRST_PAGE: "firstPage", ONCE: "once", LAST_PAGE: "lastPage" };
const PLACEMENT = { AFTER_CONTENT: "afterContent", PAGE_BOTTOM: "pageBottom" };
const ITEMS = "items";

// Rounding slack when comparing heights, as in the previous preview code.
const FIT_TOLERANCE = 0.5;

// ---- Pagination ---------------------------------------------------------

// Splits bands into pages.
//
//   bands          normalised bands (see normalizeBand), in list order
//   pageHeight     usable page height, px
//   bandHeights    { [band id]: px } for every band except the items band
//   rowHeights     px per item row, in order (rows may differ in height)
//   tableChrome    px the items table needs on every page it appears on
//                  (heading row, borders)
//   emptyTopInset  px left free at the top of a page that prints no top band
//
// Rules:
// - Bands above the items band with placement "afterContent" are top bands:
//   "everyPage" ones print on every page, "firstPage"/"once" ones on page 1.
// - Only the items band splits, row by row. A row never splits. When page 1
//   has no room for the first row, the table starts on page 2; a row too tall
//   for any page is placed anyway (and overflows), so layout always ends.
// - Bands after the items band with placement "afterContent" follow the last
//   row in list order, each moving whole to a new page when it doesn't fit.
// - "pageBottom" bands are pinned to the bottom, stacked upwards in list
//   order: "everyPage" on every page (their height is kept free on every
//   page), "firstPage" on page 1, "lastPage"/"once" on the final page only.
// - A new page started by overflow prints only the repeating bands plus the
//   content that moved.
//
// Returns { pages: [{ blocks: [{ bandId, top, height, firstRow?, rowCount? }] }] }
// with blocks in print order and `top` in px from the top of the page.
export function paginateBands({ bands, pageHeight, bandHeights = {}, rowHeights = [], tableChrome = 0, emptyTopInset = 0 }) {
  const list = bands || [];
  const heightOf = (band) => Number(bandHeights[band.id]) || 0;

  let itemsIndex = list.findIndex((band) => band.type === ITEMS);
  const itemsBand = itemsIndex >= 0 ? list[itemsIndex] : null;
  if (itemsIndex < 0) itemsIndex = list.length;

  const isFlow = (band) => band.placement !== PLACEMENT.PAGE_BOTTOM;
  const topBands = list.filter((band, i) => i < itemsIndex && isFlow(band));
  const afterBands = list.filter((band, i) => i > itemsIndex && isFlow(band));
  const bottomBands = list.filter((band) => !isFlow(band));

  const topPrints = (band, pageIndex) => band.repeat === REPEAT.EVERY_PAGE || pageIndex === 0;
  // Bottom bands on a page that isn't (or may not be) the final one.
  const bottomPrintsBeforeEnd = (band, pageIndex) =>
    band.repeat === REPEAT.EVERY_PAGE || (band.repeat === REPEAT.FIRST_PAGE && pageIndex === 0);
  const bottomPrintsOnFinal = (band, pageIndex) =>
    bottomPrintsBeforeEnd(band, pageIndex) || band.repeat === REPEAT.LAST_PAGE || band.repeat === REPEAT.ONCE;

  const reserved = (pageIndex) =>
    bottomBands.filter((band) => bottomPrintsBeforeEnd(band, pageIndex)).reduce((sum, band) => sum + heightOf(band), 0);

  const pages = [];
  const newPage = (pageIndex) => {
    const page = { index: pageIndex, blocks: [], used: 0, fresh: true };
    const printed = topBands.filter((band) => topPrints(band, pageIndex));
    if (printed.length === 0) page.used = emptyTopInset;
    printed.forEach((band) => {
      page.blocks.push({ bandId: band.id, top: page.used, height: heightOf(band) });
      page.used += heightOf(band);
    });
    return page;
  };
  const place = (page, block) => {
    page.blocks.push({ ...block, top: page.used });
    page.used += block.height;
    page.fresh = false;
  };
  const fits = (page, height, extraBottom = 0) =>
    page.used + height + reserved(page.index) + extraBottom <= pageHeight + FIT_TOLERANCE;

  let page = newPage(0);
  const breakPage = () => {
    pages.push(page);
    page = newPage(page.index + 1);
  };

  // Items: as many whole rows as fit on each page.
  if (itemsBand) {
    // Rows get no rounding slack (only whole bands do); the tiny epsilon
    // only absorbs floating-point error from adding row heights up.
    const roomFor = (p) => pageHeight - reserved(p.index) - p.used - tableChrome + 1e-9;
    // Room on any page after the first that holds nothing but its repeating
    // bands: the most a row can ever get once page 1 is passed.
    const laterPageRoom = roomFor(newPage(1));

    let next = 0;
    for (;;) {
      const room = roomFor(page);
      let take = 0;
      let rowsHeight = 0;
      while (next + take < rowHeights.length && rowsHeight + rowHeights[next + take] <= room) {
        rowsHeight += rowHeights[next + take];
        take++;
      }
      // A row that doesn't fit on an empty page is forced onto it only when
      // it wouldn't fit on any later page either (otherwise layout would
      // never end); if it would, it moves on to the next page.
      const row = rowHeights[next];
      if (take === 0 && page.fresh && next < rowHeights.length && (page.index > 0 || row > laterPageRoom)) {
        rowsHeight = row;
        take = 1;
      }

      const remaining = rowHeights.length - next;
      if (take > 0 || remaining === 0) {
        place(page, { bandId: itemsBand.id, height: tableChrome + rowsHeight, firstRow: next, rowCount: take });
        next += take;
      }
      if (next >= rowHeights.length) break;
      breakPage();
    }
  }

  // Content after the items, each band whole.
  afterBands.forEach((band) => {
    if (!fits(page, heightOf(band)) && !page.fresh) breakPage();
    place(page, { bandId: band.id, height: heightOf(band) });
  });

  // The final page also needs room for the bands pinned to its bottom.
  const finalOnly = bottomBands
    .filter((band) => bottomPrintsOnFinal(band, page.index) && !bottomPrintsBeforeEnd(band, page.index))
    .reduce((sum, band) => sum + heightOf(band), 0);
  if (finalOnly > 0 && !fits(page, 0, finalOnly) && !page.fresh) breakPage();
  pages.push(page);

  // Pin bottom bands, stacked upwards in list order.
  pages.forEach((p, i) => {
    const isFinal = i === pages.length - 1;
    const printed = bottomBands.filter((band) => (isFinal ? bottomPrintsOnFinal(band, i) : bottomPrintsBeforeEnd(band, i)));
    let top = pageHeight - printed.reduce((sum, band) => sum + heightOf(band), 0);
    printed.forEach((band) => {
      p.blocks.push({ bandId: band.id, top, height: heightOf(band) });
      top += heightOf(band);
    });
  });

  return { pages: pages.map((p) => ({ blocks: p.blocks })) };
}

// ---- Content inside a band ---------------------------------------------

// Lays out a band's content after data is merged.
//
//   elements  [{ id, top, left, width, height,      designed box, px
//                newHeight?,                        measured height after merging
//                removed?,                          collapsed: blank and marked
//                closeUp?,                          with removed: hidden by a condition, as
//                                                   part of this block (getConditionalHides)
//                shape?,                            a box (holds what's inside it)
//                stretch?,                          a box that grows with what's inside
//                keepInRow?,                        moves at least as far as its row
//                textWidth?, newTextWidth?,         text width as designed / after merging, px
//                textAlign?,                        "left" (default), "center" or "right"
//                followLeft?, followRight?,         keeps its gap to the element on that side
//                matchRow? }]                       a box kept level with the shapes beside it
//   canGrow   the band's setting
//
// In a band that can't grow nothing moves: removed elements just disappear
// and taller text is clipped by the caller. In a band that can grow:
// - An element moves by the change of the elements directly above it: those
//   ending above its top, overlapping it horizontally, with nothing else in
//   between. It moves by the largest such change, so side-by-side elements
//   (a To and a From address) don't affect each other.
// - A taller or shorter element passes on its own change in height; a
//   removed one passes on minus its height, so what was below it moves up by
//   that much (the gaps around it stay).
// - Elements removed by a condition (`closeUp`, the same key for every
//   element hidden together) close up as one block: what's below the block
//   moves up by the block's height plus the smaller of the gaps above and
//   below it, so with evenly spaced rows the next row takes the block's
//   place, and a larger designed gap (before a Total row, or before what
//   follows the last row) is kept. With nothing above the block, the gap
//   below goes, so what's below moves to the block's top.
// - An element inside a box (its top-left corner inside) with nothing above
//   it in that box moves at least as far as the box, so a box's contents
//   never get left behind. The elements under it follow it, so they can
//   still move up inside the box when something above them is removed.
// - Seen from outside, a stretching box stands in for its contents: what's
//   below it moves by the box's change, not by the contents' own.
// - An element marked `keepInRow` moves at least as far as the elements
//   beside it in its row (overlapping it vertically, not horizontally, and
//   not boxes or marked themselves), so a "Tel" beside an email that a taller
//   address above pushed down stays level with the email.
// - A shape marked `stretch` grows (or shrinks) so the gap between its bottom
//   and the lowest element inside it stays the same; when that element is
//   removed, the next lowest one counts.
// - A shape marked `matchRow` (a box beside another box) moves at least as
//   far as the shapes beside it in its designed row, and grows so its bottom
//   is level with the lowest of them. Both use the other shapes' own move
//   and size, so two boxes can match each other: side-by-side boxes end up
//   the same height when either one stretches.
// - The band changes height by as much as its lowest content moved (callers
//   keep the band's height at 0 or more).
//
// Sideways, see sidewaysLayout() below: an element marked `followLeft` or
// `followRight` keeps its gap to the element on that side when that
// element's text width changes.
//
// Returns { elements: [{ id, top, left, height }] (removed ones left out), heightChange }.
export function layoutBandContent(elements, { canGrow = false } = {}) {
  const items = (elements || []).map((el) => ({
    ...el,
    bottom: el.top + el.height,
    right: el.left + el.width,
  }));

  const leftOf = sidewaysLayout(items);

  if (!canGrow) {
    return {
      elements: items.filter((el) => !el.removed).map((el) => ({ id: el.id, top: el.top, left: leftOf(el), height: el.height })),
      heightChange: 0,
    };
  }

  const overlapsX = (a, b) => a.left < b.right && b.left < a.right;
  const isAbove = (a, b) => a !== b && a.bottom <= b.top && overlapsX(a, b);
  const isInside = (el, shape) =>
    el !== shape && el.top >= shape.top && el.top < shape.bottom && el.left >= shape.left && el.left < shape.right;
  const isBox = (el) => el.shape || el.stretch;
  // A stretching box stands in for its contents: from outside it, only the
  // box counts, so what's below moves with the box's new size.
  const hiddenFrom = (other, el) => items.some((s) => s.stretch && isInside(other, s) && !isInside(el, s));
  // Elements directly above `el`: above it with no other element above `el`
  // that sits fully between the two.
  const directlyAbove = (el) => {
    const above = items.filter((other) => isAbove(other, el) && !hiddenFrom(other, el));
    return above.filter((a) => !above.some((f) => f !== a && f.top >= a.bottom && f.bottom <= el.top));
  };
  // Everything whose top-left corner is inside `shape`, removed or not.
  const contained = (shape) => items.filter((el) => isInside(el, shape));
  const containers = (el) => items.filter((s) => isBox(s) && isInside(el, s));
  // The elements beside a `keepInRow` element in its designed row. Marked
  // ones are left out, so marked elements never depend on each other.
  // A marked shape doesn't follow what's inside another box either: those
  // elements move with that box and its growing contents, so following them
  // would push a box beside a stretching box down, and the stretching box
  // with it if they match.
  const insideOtherBox = (o, el) => items.some((s) => s !== el && isBox(s) && isInside(o, s) && !isInside(el, s));
  const rowNeighbours = (el) =>
    el.keepInRow
      ? items.filter((o) => o !== el && !o.keepInRow && !isBox(o) && o.top < el.bottom && el.top < o.bottom && !overlapsX(o, el) &&
        !(el.shape && insideOtherBox(o, el)))
      : [];

  // The shapes beside a `matchRow` box in its designed row (overlapping it
  // vertically, not horizontally).
  const rowShapes = (el) =>
    el.matchRow
      ? items.filter((o) => o !== el && o.shape && !o.removed && o.top < el.bottom && el.top < o.bottom && !overlapsX(o, el))
      : [];

  // Blocks of elements hidden together by a condition: their top element,
  // their designed top and bottom, and the gap between them and what's
  // directly above (null when nothing is).
  const blocks = new Map();
  const blockOf = (key) => {
    if (blocks.has(key)) return blocks.get(key);
    const members = items.filter((o) => o.removed && o.closeUp === key);
    const top = Math.min(...members.map((o) => o.top));
    const bottom = Math.max(...members.map((o) => o.bottom));
    const above = items.filter((o) => o.closeUp !== key && o.bottom <= top && members.some((m) => overlapsX(o, m)));
    const block = {
      first: members.find((o) => o.top === top),
      bottom,
      height: bottom - top,
      gapAbove: above.length ? top - Math.max(...above.map((o) => o.bottom)) : null,
    };
    blocks.set(key, block);
    return block;
  };

  // How far `el` moves because of `above`, an element directly above it. A
  // block hidden by a condition goes with the smaller of the gaps around it.
  const moveFrom = (above, el) => {
    if (!(above.removed && above.closeUp)) return shiftOf(above) + changeOf(above);
    const block = blockOf(above.closeUp);
    const gapBelow = Math.max(0, el.top - block.bottom);
    const gap = block.gapAbove === null ? gapBelow : Math.min(block.gapAbove, gapBelow);
    return shiftOf(block.first) - block.height - gap;
  };

  const shift = new Map();
  const ownShift = new Map();
  const change = new Map();
  const ownChange = new Map();

  // An element's move, leaving out what it takes from matching (`matchRow`).
  const ownShiftOf = (el) => {
    if (ownShift.has(el)) return ownShift.get(el);
    ownShift.set(el, 0); // guards against cycles in odd layouts
    const above = directlyAbove(el);
    const moves = [
      ...above.map((a) => moveFrom(a, el)),
      // A box only holds up the elements at its top: one with something
      // above it in the same box moves with the box through what's above
      // it, and may move up inside it when that is removed. "Above" counts
      // everything above in the box, not just what's directly above: a line
      // crossing the box can sit in between.
      ...containers(el).filter((s) => !items.some((o) => isAbove(o, el) && isInside(o, s))).map(shiftOf),
      ...rowNeighbours(el).map(shiftOf),
    ];
    const value = moves.length ? Math.max(...moves) : 0;
    ownShift.set(el, value);
    return value;
  };

  // A `matchRow` box also moves at least as far as the shapes beside it
  // (their own moves, so two matching boxes can't depend on each other).
  const shiftOf = (el) => {
    if (shift.has(el)) return shift.get(el);
    shift.set(el, ownShiftOf(el));
    const value = Math.max(ownShiftOf(el), ...rowShapes(el).map(ownShiftOf));
    shift.set(el, value);
    return value;
  };

  // Bottom after moving and its own growing, before matching.
  const ownBottomOf = (el) => el.bottom + shiftOf(el) + ownChangeOf(el);

  // A `matchRow` box grows so its bottom is level with the lowest of the
  // shapes beside it (by their own size, before they match anything).
  const changeOf = (el) => {
    if (change.has(el)) return change.get(el);
    change.set(el, ownChangeOf(el));
    const others = rowShapes(el);
    const value = others.length && !el.removed
      ? Math.max(ownBottomOf(el), ...others.map(ownBottomOf)) - (el.bottom + shiftOf(el))
      : ownChangeOf(el);
    change.set(el, value);
    return value;
  };

  const ownChangeOf = (el) => {
    if (ownChange.has(el)) return ownChange.get(el);
    ownChange.set(el, 0);
    let value = 0;
    if (el.removed) value = -el.height;
    else if (el.stretch) {
      // The old lowest point counts removed elements too, so the box shrinks
      // when its last line goes; the new one counts only what's left. With
      // nothing left the box closes up to where its content started.
      const inside = contained(el);
      if (inside.length) {
        const left = inside.filter((c) => !c.removed);
        const oldLowest = Math.max(...inside.map((c) => c.bottom));
        const newLowest = left.length
          ? Math.max(...left.map((c) => c.bottom + shiftOf(c) + changeOf(c)))
          : Math.min(...inside.map((c) => c.top + shiftOf(c)));
        value = newLowest - oldLowest - shiftOf(el);
      }
    } else if (typeof el.newHeight === "number") value = el.newHeight - el.height;
    ownChange.set(el, value);
    return value;
  };

  const kept = items.filter((el) => !el.removed);
  const result = kept.map((el) => ({
    id: el.id,
    top: el.top + shiftOf(el),
    left: leftOf(el),
    height: Math.max(0, el.height + changeOf(el)),
  }));

  const oldLowest = items.length ? Math.max(...items.map((el) => el.bottom)) : 0;
  const newLowest = result.length ? Math.max(...result.map((el) => el.top + el.height)) : 0;

  return { elements: result, heightChange: newLowest - oldLowest };
}

// ---- Sideways: following the element on the left or right ----------------

// An element marked `followLeft` keeps its designed gap to the element on its
// left (its leader) when the leader's text comes out shorter or longer than
// the {placeholders} it was designed with: "{AddressLine1}," then
// "{AddressLine2}" print as "Flat 2, 5 Park Road", not with the gap the long
// placeholder left. `followRight` is the mirror image: the element keeps its
// gap to the element on its right, e.g. a "Total:" label before a
// right-aligned {NetTotal}. This applies in every band, growing or not.
//
// - The leader is the nearest element on that side in the designed row:
//   overlapping vertically, entirely on that side of the follower's box, and
//   not a shape.
// - The leader's text edge facing the follower moves by the leader's own
//   sideways move plus what its text-width change (`newTextWidth −
//   textWidth`, both measured by the caller; missing means no change) does to
//   that edge given its `textAlign`: a left-aligned text grows to the right,
//   a right-aligned one to the left, a centred one half each way.
// - The follower moves by as much as that edge.
// - When the leader is removed (empty, "hide when empty"), the follower takes
//   its place: a `followLeft` element moves to the leader's left, a
//   `followRight` one so its right edge is at the leader's right.
// - An element marked both ways follows the element on its left.
// Returns (el) => its new left.
function sidewaysLayout(items) {
  const move = new Map();
  const sameRow = (o, el) => o !== el && !o.shape && o.top < el.bottom && el.top < o.bottom;
  const leaderOnLeft = (el) => {
    const candidates = items.filter((o) => sameRow(o, el) && o.right <= el.left + 0.5);
    return candidates.length ? candidates.reduce((best, o) => (o.right > best.right ? o : best)) : null;
  };
  const leaderOnRight = (el) => {
    const candidates = items.filter((o) => sameRow(o, el) && o.left >= el.right - 0.5);
    return candidates.length ? candidates.reduce((best, o) => (o.left < best.left ? o : best)) : null;
  };
  const textWidthChange = (el) => {
    const designed = typeof el.textWidth === "number" ? el.textWidth : null;
    const merged = typeof el.newTextWidth === "number" ? el.newTextWidth : designed;
    return designed === null || merged === null ? 0 : merged - designed;
  };
  // How far the text's right / left edge moves from its own width change.
  const growsRight = (el) => (el.textAlign === "right" ? 0 : el.textAlign === "center" ? 0.5 : 1);
  const rightEdgeChange = (el) => moveOf(el) + textWidthChange(el) * growsRight(el);
  const leftEdgeChange = (el) => moveOf(el) - textWidthChange(el) * (1 - growsRight(el));

  const moveOf = (el) => {
    if (move.has(el)) return move.get(el);
    move.set(el, 0); // guards against cycles in odd layouts
    let value = 0;
    if (el.followLeft) {
      const leader = leaderOnLeft(el);
      if (leader) value = leader.removed ? leader.left + moveOf(leader) - el.left : rightEdgeChange(leader);
    } else if (el.followRight) {
      const leader = leaderOnRight(el);
      if (leader) value = leader.removed ? leader.right + moveOf(leader) - el.right : leftEdgeChange(leader);
    }
    move.set(el, value);
    return value;
  };
  return (el) => el.left + moveOf(el);
}

// ---- Conditions: showing a field only when its value matches -------------

// The checks a field's `showWhen.op` can be. Number checks compare against
// `showWhen.value` as a number, text checks as text (ignoring upper/lower
// case and spaces at the ends), and "hasValue" only asks for a value.
export const CONDITION_OPS = {
  GT: "gt",
  GTE: "gte",
  LT: "lt",
  LTE: "lte",
  EQ: "eq",
  NE: "ne",
  IS: "is",
  IS_NOT: "isNot",
  CONTAINS: "contains",
  NOT_CONTAINS: "notContains",
  HAS_VALUE: "hasValue",
};

export const NUMBER_CONDITION_OPS = ["gt", "gte", "lt", "lte", "eq", "ne"];
export const TEXT_CONDITION_OPS = ["is", "isNot", "contains", "notContains"];

// How the group of a field hidden by its condition decides: hide when any
// counting condition fails (the default), or only when all of them do.
export const GROUP_HIDE_RULES = { ANY: "any", ALL: "all" };

const isPlainObject = (value) => value !== null && typeof value === "object";

// The parts of a value with something in them, as text. An address object
// (or any object or list) counts by its parts.
function textParts(value) {
  if (value === undefined || value === null) return [];
  if (typeof value === "number") return Number.isNaN(value) ? [] : [String(value)];
  if (isPlainObject(value)) return Object.values(value).flatMap(textParts);
  const text = String(value).trim();
  return text === "" ? [] : [text];
}

// No value: null, undefined, NaN, a blank string, a field missing from the
// data (the lookup gives undefined) or an object with nothing filled in.
export function isMissingValue(value) {
  return textParts(value).length === 0;
}

// A value as a number: numbers as they are; text with its currency signs,
// thousands separators and spaces taken out ("£1,234.50" is 1234.5).
// null when it isn't a number.
export function conditionNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[^\d.-]/g, "");
  return /^-?(\d+\.?\d*|\.\d+)$/.test(cleaned) ? Number(cleaned) : null;
}

const sameNumber = (a, b) => Math.abs(a - b) < 1e-9;

// Whether a field with condition `showWhen` prints, given its raw value (as
// in the print data, before number and date formats).
// - No condition (or no `op`): always.
// - No value: hidden while `hideWhenMissing` is on (the default); with it
//   off, the check is skipped and the field prints as usual. "hasValue"
//   hides on no value either way.
// - A number check on a value that isn't a number: hidden.
export function meetsCondition(showWhen, value) {
  if (!showWhen || !showWhen.op) return true;
  const { op } = showWhen;
  if (isMissingValue(value)) return op === CONDITION_OPS.HAS_VALUE ? false : showWhen.hideWhenMissing === false;
  if (op === CONDITION_OPS.HAS_VALUE) return true;

  if (NUMBER_CONDITION_OPS.includes(op)) {
    const n = conditionNumber(value);
    if (n === null) return false;
    const target = conditionNumber(showWhen.value) ?? 0;
    switch (op) {
      case CONDITION_OPS.GT: return n > target && !sameNumber(n, target);
      case CONDITION_OPS.GTE: return n > target || sameNumber(n, target);
      case CONDITION_OPS.LT: return n < target && !sameNumber(n, target);
      case CONDITION_OPS.LTE: return n < target || sameNumber(n, target);
      case CONDITION_OPS.EQ: return sameNumber(n, target);
      default: return !sameNumber(n, target);
    }
  }

  if (TEXT_CONDITION_OPS.includes(op)) {
    const text = textParts(value).join(" ").toLowerCase();
    const target = String(showWhen.value ?? "").trim().toLowerCase();
    switch (op) {
      case CONDITION_OPS.IS: return text === target;
      case CONDITION_OPS.IS_NOT: return text !== target;
      case CONDITION_OPS.CONTAINS: return text.includes(target);
      default: return !text.includes(target);
    }
  }

  return true; // an unknown check never hides anything
}

// Which elements of one band conditions hide, and how they close up.
//
//   items    every element placed in the band (text, shapes, images, QR
//            codes, logo, title, VAT summary):
//            [{ id, groupId?, showWhen?, groupHideRule? }]
//   valueOf  (item) => the raw value of a field; called only for items
//            with a condition
//
// - A field whose condition fails is hidden.
// - A group is hidden, all its members with it, when its counting
//   conditions (fields with `showWhen.hideGroup`) fail: any one of them, or
//   all of them when a member has `groupHideRule: "all"`. A group with no
//   counting condition is never hidden as a whole.
//
// Returns a Map of hidden id => block key. Pass each hidden element to
// layoutBandContent() with `removed: true, closeUp: key`; a hidden group is
// one block, a field hidden on its own is a block by itself.
export function getConditionalHides(items, valueOf) {
  const list = items || [];
  const failed = new Set(
    list.filter((item) => item.showWhen && item.showWhen.op && !meetsCondition(item.showWhen, valueOf(item))).map((item) => item.id)
  );

  const hidden = new Map();
  list.forEach((item) => {
    if (failed.has(item.id)) hidden.set(item.id, "field:" + item.id);
  });

  const groups = new Map();
  list.forEach((item) => {
    if (!item.groupId) return;
    if (!groups.has(item.groupId)) groups.set(item.groupId, []);
    groups.get(item.groupId).push(item);
  });
  groups.forEach((members, groupId) => {
    const counting = members.filter((m) => m.showWhen && m.showWhen.op && m.showWhen.hideGroup);
    if (counting.length === 0) return;
    const all = members.some((m) => m.groupHideRule === GROUP_HIDE_RULES.ALL);
    const hide = all ? counting.every((m) => failed.has(m.id)) : counting.some((m) => failed.has(m.id));
    if (hide) members.forEach((m) => hidden.set(m.id, "group:" + groupId));
  });

  return hidden;
}

// ---- Empty lines -------------------------------------------------------

// Placeholders as the backend merges them: {Field}, word characters only.
const PLACEHOLDER = /\{(\w+)\}/g;

// Merges values into a multi-line text with {Field} placeholders and drops
// every line whose placeholders all came out empty, label text included
// ("Tel: {Phone}" disappears without a phone number). Lines without a
// placeholder, or with at least one filled placeholder, are kept; on those,
// spaces an empty placeholder leaves behind are tidied ("{City} {Postcode}"
// with no city gives "W1", not " W1").
//
// `values` is either a function (field) => string, so each side plugs in its
// own lookup and formatting (data paths, address objects, decimals), or a
// plain { field: value } map. A value may itself span several lines.
// Missing values merge as "".
export function mergeCollapsingLines(text, values = {}) {
  const lookup = typeof values === "function" ? values : (field) => values[field];
  const valueOf = (field) => {
    const value = lookup(field);
    return value === undefined || value === null ? "" : String(value);
  };

  return (text || "")
    .split("\n")
    .flatMap((line) => {
      const fields = [...line.matchAll(PLACEHOLDER)].map((m) => m[1]);
      if (fields.length === 0) return [line];

      const filled = fields.map((field) => valueOf(field).trim() !== "");
      if (!filled.some(Boolean)) return [];

      const merged = line.replace(PLACEHOLDER, (match, field) => valueOf(field));
      if (filled.every(Boolean)) return [merged];
      return [merged.replace(/ {2,}/g, " ").trim()];
    })
    .join("\n");
}
